<script setup>
import { storeToRefs } from 'pinia'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUIStore } from '@/core/stores/ui.store'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { BRADLEY_ANALYTICS_EVENTS } from '@/modules/new-bradley/analytics/bradley-analytics.constants.js'
import BradleyLogoIcon from '@/modules/new-bradley/components/bradley-logo-icon.vue'
import { useAnalytics } from '@/shared/analytics'
import {
  APP_HEADER_ID,
  APP_SIDEBAR_ID,
  clampSnappedPosition,
  freePositionToStyle,
  getContentBounds,
  positionToStyle,
  snapPositionFromRect,
  useBradleyFloatingWidgetPosition,
} from '../composables/use-bradley-floating-widget-position.js'

const DRAG_THRESHOLD_PX = 6
const SIDEBAR_TRANSITION_MS = 320
const LAYOUT_RECONCILE_DEBOUNCE_MS = 50

const uiStore = useUIStore()
const authStore = useAuthStore()
const { t } = useI18n()
const { trackEvent } = useAnalytics()

const { defaultPosition: storedDefaultPosition, persistPosition } = useBradleyFloatingWidgetPosition()

const { bradleyFloatingWidgetVisible, bradleyPanelOpen, sidebarCollapsed } = storeToRefs(uiStore)

const widgetReference = ref(null)
const snappedPosition = ref(storedDefaultPosition ?? null)
const dragStyle = ref(null)
const isDragging = ref(false)
const widgetSize = ref({ width: 0, height: 0 })
const layoutBoundsVersion = ref(0)

let pointerStartX = 0
let pointerStartY = 0
let dragPointerOffsetX = 0
let dragPointerOffsetY = 0
let didExceedDragThreshold = false
let layoutReconcileTimer = null
let layoutResizeObserver = null

const showWidget = computed(() => {
  const enabled = Boolean(authStore.tenantData?.enabledBradleyAccess)
  return enabled && bradleyFloatingWidgetVisible.value && !bradleyPanelOpen.value
})

const widgetInlineStyle = computed(() => {
  void layoutBoundsVersion.value
  void sidebarCollapsed.value

  if (dragStyle.value) {
    return dragStyle.value
  }

  if (!snappedPosition.value) {
    return { right: '24px', bottom: '24px', left: 'auto', top: 'auto' }
  }

  const { width, height } = widgetSize.value
  if (width <= 0 || height <= 0) {
    return { right: '24px', bottom: '24px', left: 'auto', top: 'auto' }
  }

  return positionToStyle(snappedPosition.value, width, height)
})

function measureWidget() {
  const element = widgetReference.value
  if (!element) {
    return
  }
  const rect = element.getBoundingClientRect()
  widgetSize.value = { width: rect.width, height: rect.height }
}

function applySnappedPosition(position) {
  snappedPosition.value = position
  dragStyle.value = null
  persistPosition(position)
  nextTick(measureWidget)
}

function clampDragStyle(leftPx, topPx) {
  const { width, height } = widgetSize.value
  if (width <= 0 || height <= 0) {
    return
  }
  dragStyle.value = freePositionToStyle(leftPx, topPx, width, height)
}

function finishDrag() {
  const element = widgetReference.value
  if (!element) {
    isDragging.value = false
    return
  }

  const rect = element.getBoundingClientRect()
  applySnappedPosition(snapPositionFromRect(rect))
  isDragging.value = false
}

function onPointerDown(event) {
  if (event.button !== 0) {
    return
  }

  const element = widgetReference.value
  if (!element) {
    return
  }

  measureWidget()
  const rect = element.getBoundingClientRect()
  pointerStartX = event.clientX
  pointerStartY = event.clientY
  dragPointerOffsetX = event.clientX - rect.left
  dragPointerOffsetY = event.clientY - rect.top
  didExceedDragThreshold = false
  isDragging.value = true

  element.setPointerCapture(event.pointerId)
  event.preventDefault()
}

function onPointerMove(event) {
  if (!isDragging.value) {
    return
  }

  const deltaX = Math.abs(event.clientX - pointerStartX)
  const deltaY = Math.abs(event.clientY - pointerStartY)
  if (deltaX > DRAG_THRESHOLD_PX || deltaY > DRAG_THRESHOLD_PX) {
    didExceedDragThreshold = true
  }

  clampDragStyle(
    event.clientX - dragPointerOffsetX,
    event.clientY - dragPointerOffsetY,
  )
}

function onPointerUp(event) {
  const element = widgetReference.value
  if (!element?.hasPointerCapture(event.pointerId)) {
    return
  }

  element.releasePointerCapture(event.pointerId)

  if (isDragging.value) {
    finishDrag()
  }
}

function onPointerCancel(event) {
  const element = widgetReference.value
  if (element?.hasPointerCapture(event.pointerId)) {
    element.releasePointerCapture(event.pointerId)
  }
  if (isDragging.value) {
    dragStyle.value = null
    isDragging.value = false
  }
}

function handleOpen() {
  if (didExceedDragThreshold) {
    didExceedDragThreshold = false
    return
  }

  trackEvent(BRADLEY_ANALYTICS_EVENTS.ASK_BRADLEY_AI_BUTTON, { source: 'floating_widget' })
  uiStore.openBradleyPanel({ startNewChat: false })
}

function bumpLayoutBounds() {
  layoutBoundsVersion.value += 1
}

function reconcilePositionWithLayout() {
  if (!showWidget.value || isDragging.value || !snappedPosition.value) {
    return
  }

  measureWidget()
  const { width, height } = widgetSize.value
  if (width <= 0 || height <= 0) {
    return
  }

  bumpLayoutBounds()

  const element = widgetReference.value
  if (element) {
    const rect = element.getBoundingClientRect()
    const { minTop, minLeft, maxTop, maxLeft } = getContentBounds(width, height)

    if (rect.left < minLeft - 0.5 || rect.top < minTop - 0.5) {
      const clampedLeft = Math.min(maxLeft, Math.max(minLeft, rect.left))
      const clampedTop = Math.min(maxTop, Math.max(minTop, rect.top))
      dragStyle.value = null
      snappedPosition.value = snapPositionFromRect({ left: clampedLeft, top: clampedTop, width, height })
      persistPosition(snappedPosition.value)
      return
    }
  }

  const clamped = clampSnappedPosition(snappedPosition.value, width, height)
  if (
    clamped.edge !== snappedPosition.value.edge
    || clamped.offsetPx !== snappedPosition.value.offsetPx
  ) {
    snappedPosition.value = clamped
    persistPosition(clamped)
  }
}

function scheduleLayoutReconcile(delayMs = LAYOUT_RECONCILE_DEBOUNCE_MS) {
  if (!showWidget.value || isDragging.value) {
    return
  }

  bumpLayoutBounds()
  if (layoutReconcileTimer) {
    clearTimeout(layoutReconcileTimer)
  }
  layoutReconcileTimer = setTimeout(() => {
    layoutReconcileTimer = null
    reconcilePositionWithLayout()
  }, delayMs)
}

function observeLayoutTargets() {
  layoutResizeObserver?.disconnect()
  layoutResizeObserver = null

  if (!showWidget.value) {
    return
  }

  layoutResizeObserver = new ResizeObserver(() => {
    scheduleLayoutReconcile()
  })

  for (const id of [APP_SIDEBAR_ID, APP_HEADER_ID]) {
    const element = document.getElementById(id)
    if (element) {
      layoutResizeObserver.observe(element)
    }
  }
}

function onWindowResize() {
  scheduleLayoutReconcile(0)
}

onMounted(() => {
  nextTick(() => {
    measureWidget()
    observeLayoutTargets()
    reconcilePositionWithLayout()
  })
  window.addEventListener('resize', onWindowResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResize)
  layoutResizeObserver?.disconnect()
  if (layoutReconcileTimer) {
    clearTimeout(layoutReconcileTimer)
  }
})

watch(showWidget, (visible) => {
  if (visible) {
    nextTick(() => {
      measureWidget()
      observeLayoutTargets()
      reconcilePositionWithLayout()
    })
  }
  else {
    layoutResizeObserver?.disconnect()
  }
})

watch(sidebarCollapsed, () => {
  scheduleLayoutReconcile()
  setTimeout(reconcilePositionWithLayout, SIDEBAR_TRANSITION_MS)
})

watch(bradleyPanelOpen, (isOpen, wasOpen) => {
  if (wasOpen && !isOpen && bradleyFloatingWidgetVisible.value) {
    snappedPosition.value = null
    dragStyle.value = null
    persistPosition(null)
  }
})
</script>

<template>
  <teleport to="body">
    <button
      v-if="showWidget"
      ref="widgetReference"
      type="button"
      class="bradley-floating-widget"
      :class="{ 'bradley-floating-widget--dragging': isDragging }"
      :style="widgetInlineStyle"
      :aria-label="t('newBradley.panel.resumeBradley')"
      :title="t('newBradley.panel.resumeBradley')"
      @click="handleOpen"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerCancel"
    >
      <bradley-logo-icon
        class="bradley-floating-widget__logo"
        :height="16"
      />
      <span
        class="bradley-floating-widget__chevron-box"
        aria-hidden="true"
      >
        <i class="pi pi-chevron-up bradley-floating-widget__chevron" />
      </span>
    </button>
  </teleport>
</template>

<style scoped>
.bradley-floating-widget {
  position: fixed;
  z-index: 120;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 3rem;
  padding: 0.5rem 0.625rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.875rem;
  cursor: grab;
  color: #1f2937;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
  touch-action: none;
  user-select: none;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease,
    left 0.3s ease,
    top 0.3s ease;
}

.bradley-floating-widget:hover:not(.bradley-floating-widget--dragging) {
  transform: translateY(-1px);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.22);
}

.bradley-floating-widget--dragging {
  cursor: grabbing;
  transform: none;
  transition: box-shadow 0.2s ease;
}

.bradley-floating-widget:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 3px;
}

.bradley-floating-widget__logo {
  color: #0000cc;
  pointer-events: none;
}

.bradley-floating-widget__chevron-box {
  width: 1.2rem;
  height: 1.2rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.3rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  background: #f8fafc;
  pointer-events: auto;
  cursor: pointer;
}

.bradley-floating-widget__chevron {
  font-size: 0.6rem;
}
</style>
